import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Container,
    Paper, useMediaQuery
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import axios from '@shared/config/axios';
import { styled } from '@mui/material/styles';
import { LoadingPage } from '@widgets/loading-page';

const StyledTableCell = styled(TableCell)(({ theme }) => ( {
    fontSize: '0.75rem',
    padding: '4px',
    height: '32px',
    [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
        padding: '8px',
        height: '48px',
    },
} ));

export const TypesPage = () => {
    const queryClient = useQueryClient();
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({ name: '' });

    const { data: types = [], isLoading } = useQuery({
        queryKey: ['types'],
        queryFn: async () => {
            const { data } = await axios.get('/types');
            return data;
        },
    });

    const addMutation = useMutation({
        mutationFn: (newType) => axios.post('/types', newType),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['types'] }),
    });

    const updateMutation = useMutation({
        mutationFn: (updatedType) => axios.patch(`/types/${updatedType._id}`, { name: updatedType.name }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['types'] }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => axios.delete(`/types/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['types'] }),
    });

    const handleOpen = (data = null) => {
        setEditData(data);
        setFormData(data || { name: '' });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFormData({ name: '' });
    };

    const handleSubmit = () => {
        if (editData) {
            updateMutation.mutate({ ...formData, id: editData._id });
        } else {
            addMutation.mutate(formData);
        }
        handleClose();
    };

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    if (isLoading) return <LoadingPage/>;

    return (
        <>
            <Container
                maxWidth={false}
                sx={{
                    width: '100%',
                    padding: { xs: 1, sm: 2, md: 3 },
                    height: '80vh',
                }}
            >
                <Paper
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden', // Ограничивает переполнение
                    }}
                >
                    <Box sx={{
                        padding: { xs: 1, md: 2 },
                        borderBottom: '1px solid #ccc',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <Button
                            startIcon={<Add/>}
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpen()}
                            size={isSmallScreen ? 'small' : 'medium'}
                            sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}
                        >
                            add type
                        </Button>
                    </Box>

                    {/* Нижняя часть с таблицей */}
                    <Box
                        sx={{ flex: 1, ml: 1, overflowX: 'auto', overflowY: 'auto' }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>id</StyledTableCell>
                                    <StyledTableCell>name</StyledTableCell>
                                    <StyledTableCell>actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {types?.map((type) => (
                                    <TableRow key={type._id}>
                                        <StyledTableCell>{type._id.slice(-5)}</StyledTableCell>
                                        <StyledTableCell>{type.name}</StyledTableCell>
                                        <StyledTableCell>
                                            <IconButton size={isSmallScreen ? 'small' : 'medium'} color="primary"
                                                        onClick={() => handleOpen(type)}>
                                                <Edit/>
                                            </IconButton>
                                            <IconButton size={isSmallScreen ? 'small' : 'medium'} color="error"
                                                        onClick={() => handleDelete(type._id)}>
                                                <Delete/>
                                            </IconButton>
                                        </StyledTableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>

                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>{editData ? 'edit type' : 'add type'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="secondary">
                            cancel
                        </Button>
                        <Button onClick={handleSubmit} color="primary" variant="contained">
                            {editData ? 'update' : 'add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    );
};


